import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal } from 'components/Modal'
import { DetectBlur } from 'containers/util-containers'
import { FixedAddonForm } from './types'
import { Form } from 'components/Form'
import { DatePicker, NumberInput, TextArea } from 'components'
import { SalaryCodeSelect } from 'components/Select/SalaryCodeSelect'
import styled from 'styled-components'
import { set } from 'date-fns'

interface FieldProps {
  alignLeft?: boolean
  alignRight?: boolean
  area?: string
}
export const Field = styled.div<FieldProps>`
  grid-area: ${props => props.area};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${props => props.alignLeft && 'align-items: flex-start;'}
  ${props => props.alignRight && 'align-items: flex-end;'}

  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    color: ${props => props.theme.colors.gray5};
  }
`

const StyledForm = styled(Form)`
  gap: 0.5rem;
`

const DateTimeField = styled(Field)`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 0.5rem;

  label {
    grid-row: 1;
    grid-column: 1 / 3;
    margin: 0 0 0 0;
  }
  ${props => props.theme.media.mobile} {
    .--time-picker-inner-wrapper {
      width: 80px;
      padding: 0;
    }
  }
`

interface EditFixedAddonPromptProps {
  initialData?: FixedAddonForm
  onSubmit: (data: FixedAddonForm | null) => void
}

export const EditFixedAddonPrompt: React.FC<EditFixedAddonPromptProps> = ({
  initialData,
  onSubmit,
}) => {
  const { formValues, formChangeHandler, formValid, updateForm } =
    useForm<FixedAddonForm>({
      values: initialData ?? {
        date: new Date(),
        salaryCode: '',
        amount: 7.5,
        comment: '',
      },
      config: {
        initAsInvalid: true,
      },
      validators: {
        date: value => {
          return value ? null : translations.error.required
        },
        salaryCode: value => {
          return value ? null : translations.error.required
        },
      },
    })

  const translations = useTranslate({
    label: {
      date: 'common.date',
      salaryCode: 'common.salary-code',
      amount: 'common.amount',
      comment: 'common.comment',
    },
    create: 'common.create-alt',
    update: 'common.update',

    error: {
      startBeforeEnd: 'timesheets.error.start-before-end',
      required: 'common.required',
    },
  })

  function updateDate(key: 'date') {
    return (value: Date) => {
      const existingValue = formValues[key]
      const newValue = set(existingValue, {
        year: value.getFullYear(),
        month: value.getMonth(),
        date: value.getDate(),
      })

      updateForm({
        [key]: newValue,
      })
    }
  }

  return (
    <DetectBlur active onClickOutside={() => onSubmit(null)}>
      <RegularModal
        title={translations.create}
        submitText={translations.update}
        submitDisabled={!formValid}
        onCancel={() => onSubmit(null)}
        onSubmit={() => onSubmit(formValues)}
      >
        <StyledForm onSubmit={() => onSubmit(formValues)}>
          <Field></Field>
          <DateTimeField>
            <label>{translations.label.date}</label>

            <DatePicker
              value={formValues.date}
              onChange={updateDate('date')}
              grayed
            />
          </DateTimeField>
          <Field>
            <label>{translations.label.amount}</label>
            <NumberInput
              value={formValues.amount}
              onChange={formChangeHandler('amount')}
              grayed
            />
          </Field>
          <Field>
            <label>{translations.label.salaryCode}</label>
            <SalaryCodeSelect
              value={formValues.salaryCode}
              onChange={value =>
                value ? formChangeHandler('salaryCode')(value) : null
              }
              grayed
            />
          </Field>
          <Field>
            <label>{translations.label.comment}</label>
            <TextArea
              value={formValues.comment}
              onChange={formChangeHandler('comment')}
              rows={3}
            />
          </Field>
        </StyledForm>
      </RegularModal>
    </DetectBlur>
  )
}
